import { PayloadedAction } from '../../shared/types'
import {
  AUTH,
  AUTH_NAME_SEARCH,
  AUTH_OEM,
  AUTH_RENTAL,
  AUTH_RESET,
  AUTH_VAS,
  CLEAR_ODOMETER_LOOKUP,
  DISMISS_PASSKEY_REGISTRATION_PROMPT,
  HYDRATE_PASSKEY_DISMISSED,
  INCREMENT_ERROR_COUNT,
  PASSKEY_AUTH,
  PASSKEY_LOGIN_COMPLETE,
  PASSKEY_REGISTER,
  REQUEST_ODOMETER_BY_PLATE,
  REQUEST_ODOMETER_BY_VIN,
  RESET_AUTH_BY_PLATE_VEHICLES,
  RESET_ERROR_COUNT,
  SET_AGENT_FIRST_NAME,
  SET_AGENT_LAST_NAME,
  SET_AUTH,
  SET_AUTH_BY_PLATE_VEHICLES,
  SET_AUTH_METHOD,
  SET_CLICKED_USE_ANOTHER_AUTH_FORM,
  SET_ENCRYPTED_MEMBER_NUMBER,
  SET_LAST_SEARCH_ID,
  SET_ODOMETER_FLOOR,
  SET_ODOMETER_VEHICLES,
  SET_PASSKEY_SUPPORTED,
  SET_RAP_AUTH,
  SET_RAP_CONFIRM_ODOMETER,
  SET_SECURE_PARAMS,
  SECURE_AUTH_ENDED,
  PASSKEY_REGISTRATION_PROMPT,
} from './auth.actions'
import { AgentSettingsParams, AuthMethods, OdometerLookupVehicle } from './auth.types'
import { Eligibility, Vehicle } from './eligibility.types'
import { MEMBER_ELIGIBILITY_RESULT, MEMBER_INFO } from '../member/member.actions'
import { ACTIVE_CALL_STATUS } from '../dashboard/calls-statuses/call-status.actions'
import { LOAD_MODE_CONFIGURATION } from './mode-configuration.actions'
import { VendorConfigurationMode } from './mode-configuration.types'

export interface OdometerLookup {
  loading: boolean
  floor: number | null
  vehicles: OdometerLookupVehicle[] | null
  resolvedVin: string | null
}

export interface AuthState {
  club: string
  accessToken: string
  noRouting?: boolean
  expiresIn: number
  isSecure: boolean
  method: AuthMethods
  memberNumber?: string
  memId?: string
  encryptedMemberNumber?: string
  ets?: string
  odometer?: number
  agentSettings?: AgentSettingsParams
  modeConfig: VendorConfigurationMode
  eligibility?: Eligibility
  isRapTokenAccess: boolean
  errorCount: number
  isUseAnotherFormClickedAfterError: boolean
  lastSearchId: string
  authByPlateVehicles: Vehicle[]
  odometerLookup: OdometerLookup
  isPasskeySupported: boolean
  authenticatedViaPasskey: boolean
  // Transient latch: true from passkey verification success through the regular
  // login that follows, until the app navigates off the sign-in screen or the login
  // ends on it with an eligibility result (ineligibility / coverage-exhausted dialog,
  // renewal or payment-in-process sheet) or the active-call lookup failing. Keeps the passkey loading card up (as
  // "Authenticating with Passkey") so the login form never flashes between
  // verification and navigation.
  passkeyAuthenticating: boolean
  passkeyPromptDismissed: boolean
  showPasskeyRegistrationBottomSheet: boolean
}

const initialAuthByPlateVehicles: Vehicle[] = []

const initialOdometerLookup: OdometerLookup = {
  loading: false,
  floor: null,
  vehicles: null,
  resolvedVin: null,
}

export const initialState: AuthState = {
  club: '',
  accessToken: '',
  noRouting: false,
  expiresIn: 0,
  isSecure: false,
  agentSettings: null,
  method: AuthMethods.MEMBER_NAME,
  modeConfig: null,
  isRapTokenAccess: false,
  errorCount: 0,
  isUseAnotherFormClickedAfterError: false,
  lastSearchId: null,
  authByPlateVehicles: initialAuthByPlateVehicles,
  odometerLookup: initialOdometerLookup,
  isPasskeySupported: false,
  authenticatedViaPasskey: false,
  passkeyAuthenticating: false,
  passkeyPromptDismissed: false,
  showPasskeyRegistrationBottomSheet: false,
}

export function reducer(
  state: AuthState | undefined = initialState,
  action: PayloadedAction
): AuthState {
  switch (action.type) {
    case SET_SECURE_PARAMS:
      return {
        ...state,
        memId: action.payload.memId,
        ets: action.payload.ets,
        isSecure: true,
        method: AuthMethods.AAA_NATIONAL,
      }
    case SET_ENCRYPTED_MEMBER_NUMBER:
      return {
        ...state,
        encryptedMemberNumber: action.payload.memberNumber,
        isSecure: true,
        method: AuthMethods.ENCRYPTED_MEMBER_NUMBER,
      }
    case SET_AUTH:
      return {
        ...state,
        club: action.payload.club,
        accessToken: action.payload.access_token,
        isSecure: true,
        method: AuthMethods.AAA_TOKEN,
        ...(action.payload.no_routing !== undefined ? { noRouting: action.payload.no_routing } : {}),
      }
    case SET_RAP_AUTH.REQUEST:
      return {
        ...state,
        accessToken: action.payload.access_token,
        isSecure: action.payload.isSecure,
        method: action.payload.method,
        isRapTokenAccess: !action.payload.odometer,
      }
    case SET_RAP_CONFIRM_ODOMETER:
      return {
        ...state,
        accessToken: action.payload.access_token,
        isSecure: action.payload.isSecure,
        method: action.payload.method,
        odometer: action.payload.odometer,
        isRapTokenAccess: false,
      }
    case SET_AUTH_METHOD:
      return {
        ...state,
        method: action.payload,
      }

    case LOAD_MODE_CONFIGURATION.SUCCESS:
      return {
        ...state,
        modeConfig: action.payload,
      }

    case AUTH_OEM.SUCCESS:
    case AUTH_RENTAL.SUCCESS:
    case AUTH_VAS.SUCCESS:
      return {
        ...state,
        accessToken: action.payload.access_token,
        expiresIn: action.payload.expires_in,
        isSecure: action.payload.isSecure,
        method: action.payload.method || state.method,
        eligibility: action.payload.eligibility,
        club: action.payload.club,
      }

    case AUTH.SUCCESS:
    case AUTH_NAME_SEARCH.SUCCESS:
      return {
        ...state,
        club: action.payload.club,
        accessToken: action.payload.access_token,
        expiresIn: action.payload.expires_in,
        isSecure: action.payload.isSecure,
        method: action.payload.method || state.method,
      }
    case AUTH.REQUEST:
      return {
        ...state,
        isSecure: action.payload.isSecure,
        memberNumber: action.payload.memberNumber,
        method: action.payload.method || state.method,
      }
    case AUTH.FAILURE:
    case SECURE_AUTH_ENDED:
      return {
        ...state,
        isSecure: false,
      }
    case PASSKEY_AUTH.SUCCESS:
      return {
        ...state,
        authenticatedViaPasskey: true,
        passkeyAuthenticating: true,
      }
    case PASSKEY_AUTH.FAILURE:
    case MEMBER_INFO.FAILURE:
    case MEMBER_ELIGIBILITY_RESULT:
    case ACTIVE_CALL_STATUS.FAILURE:
    case PASSKEY_LOGIN_COMPLETE:
      return {
        ...state,
        passkeyAuthenticating: false,
      }
    case SET_PASSKEY_SUPPORTED:
      return {
        ...state,
        isPasskeySupported: action.payload,
      }
    case DISMISS_PASSKEY_REGISTRATION_PROMPT:
      return {
        ...state,
        passkeyPromptDismissed: true,
        showPasskeyRegistrationBottomSheet: false,
      }
    case HYDRATE_PASSKEY_DISMISSED:
      return {
        ...state,
        passkeyPromptDismissed: action.payload,
      }
    case PASSKEY_REGISTRATION_PROMPT:
      return {
        ...state,
        showPasskeyRegistrationBottomSheet: true,
      }
    case PASSKEY_REGISTER.SUCCESS:
      return {
        ...state,
        showPasskeyRegistrationBottomSheet: false,
        authenticatedViaPasskey: true,
      }
    case PASSKEY_REGISTER.FAILURE:
      return {
        ...state,
        showPasskeyRegistrationBottomSheet: false,
      }
    case AUTH_RESET:
      return {
        ...initialState,
        modeConfig: state.modeConfig,
        method: state.method,
        isRapTokenAccess: state.isRapTokenAccess,
        errorCount: state.errorCount,
        isUseAnotherFormClickedAfterError:
          state.isUseAnotherFormClickedAfterError,
        noRouting: state.noRouting,
        isPasskeySupported: state.isPasskeySupported,
        passkeyPromptDismissed: state.passkeyPromptDismissed,
      }
    case INCREMENT_ERROR_COUNT:
      return {
        ...state,
        errorCount: state.errorCount + 1,
      }
    case RESET_ERROR_COUNT:
      return {
        ...state,
        errorCount: 0,
      }
    case SET_CLICKED_USE_ANOTHER_AUTH_FORM:
      return {
        ...state,
        isUseAnotherFormClickedAfterError: state.errorCount > 0,
      }
    case SET_AGENT_FIRST_NAME:
      return {
        ...state,
        agentSettings: {
          ...state.agentSettings,
          agentFirstName: action.payload,
        },
      }
    case SET_AGENT_LAST_NAME:
      return {
        ...state,
        agentSettings: {
          ...state.agentSettings,
          agentLastName: action.payload,
        },
      }
    case SET_LAST_SEARCH_ID:
      return {
        ...state,
        lastSearchId: action.payload,
      }
    case SET_AUTH_BY_PLATE_VEHICLES:
      return {
        ...state,
        authByPlateVehicles: action.payload,
      }
    case RESET_AUTH_BY_PLATE_VEHICLES:
      return {
        ...state,
        authByPlateVehicles: initialAuthByPlateVehicles,
      }
    case REQUEST_ODOMETER_BY_VIN:
    case REQUEST_ODOMETER_BY_PLATE:
      return {
        ...state,
        odometerLookup: {
          loading: true,
          floor: null,
          vehicles: null,
          resolvedVin: null,
        },
      }
    case SET_ODOMETER_FLOOR:
      return {
        ...state,
        odometerLookup: {
          ...state.odometerLookup,
          loading: false,
          floor: action.payload.floor,
          resolvedVin: action.payload.vin ?? null,
          vehicles: null,
        },
      }
    case SET_ODOMETER_VEHICLES:
      return {
        ...state,
        odometerLookup: {
          ...state.odometerLookup,
          loading: false,
          vehicles: action.payload,
        },
      }
    case CLEAR_ODOMETER_LOOKUP:
      return {
        ...state,
        odometerLookup: initialOdometerLookup,
      }
    default:
      return state
  }
}
